import type { FieldErrors, FieldValues } from 'react-hook-form';
import type { FieldError, FieldLabels } from './types';

/**
 * エラーオブジェクトからフラットなエラー配列を生成
 *
 * react-hook-form の FieldErrors オブジェクトを走査し、
 * ネストされたエラーも含めてフラットな配列に変換します。
 *
 * @param errors - react-hook-form のエラーオブジェクト
 * @param fieldLabels - フィールドキーとラベルのマッピング
 * @param prefix - ネストされたフィールド用のプレフィックス（内部使用）
 * @returns フラット化されたエラー配列
 *
 * @example
 * ```ts
 * const errors = {
 *   name: { message: 'は必須です', type: 'required' },
 *   address: {
 *     city: { message: 'を入力してください', type: 'required' }
 *   }
 * };
 * const labels = { name: '名前', 'address.city': '市区町村' };
 * const result = flattenErrors(errors, labels);
 * // [
 * //   { key: 'name', label: '名前', message: 'は必須です' },
 * //   { key: 'address.city', label: '市区町村', message: 'を入力してください' }
 * // ]
 * ```
 */
export const flattenErrors = <T extends FieldValues>(
  errors: FieldErrors<T>,
  fieldLabels: FieldLabels<T>,
  prefix = '',
): FieldError[] => {
  const result: FieldError[] = [];
  // 実行時にはstringキーでアクセスするため、Record型として扱う
  const labels = fieldLabels as Record<string, string | undefined>;

  for (const [key, value] of Object.entries(errors)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (value?.message && typeof value.message === 'string') {
      // リーフノード（エラーメッセージを持つフィールド）
      const label = labels[fullKey] || labels[key] || key;
      result.push({
        key: fullKey,
        label,
        message: value.message,
      });
    } else if (value && typeof value === 'object') {
      // 配列またはネストされたオブジェクト
      if (Array.isArray(value)) {
        // 配列フィールド（例: expenditure_purpose_ids）
        // 配列全体のエラーがある場合
        const arrayError = value as unknown as { message?: string };
        if (arrayError.message && typeof arrayError.message === 'string') {
          const label = labels[fullKey] || labels[key] || key;
          result.push({
            key: fullKey,
            label,
            message: arrayError.message,
          });
        }
      } else {
        // ネストされたエラー
        result.push(...flattenErrors(value as FieldErrors<FieldValues>, fieldLabels, fullKey));
      }
    }
  }

  return result;
};
